/**
 * STAX SR-L700MK2 -- dedicated electrostatic headphone profile (spec 12).
 *
 * This is deliberately NOT run through the loudspeaker path. Electrostatic
 * headphones differ from dynamic drivers in ways that matter to the DSP:
 *
 *  - The driver is a near-massless diaphragm between fixed stators, driven as a
 *    constant-charge device. Its distortion is dominated by odd-order products
 *    from field non-uniformity and is exceptionally low at normal levels, so
 *    the generic dynamic-driver shaper would overstate H2 badly.
 *  - The electrical load is capacitive, not resistive: roughly 110 pF including
 *    the cable. It cannot be driven by a normal headphone amplifier at all; it
 *    needs an energiser supplying a 580 V pro bias and several hundred volts of
 *    audio swing. The engine enforces that pairing.
 *  - There is no enclosure. The open Lambda-series housing means no cup
 *    resonance in the usual sense and essentially no seal dependence at low
 *    frequencies -- pad compression shifts the response far less than it does on
 *    a closed dynamic headphone.
 */

import type { HeadphoneProfile } from '../../types/hardware';
import { frCurve } from '../../types/curves';
import { HARMONIC_SHAPES, makeHeadphone, usd } from '../kit';

/**
 * Raw response referenced to a flat-plate / pinna-less coupler.
 *
 * Confidence is ESTIMATED: this is a consensus shape drawn from published
 * third-party measurement summaries of the Lambda-series MK2 tuning, not a
 * measurement made for this project. Import a real FR CSV in the Measurement
 * Lab to replace it -- the DSP does not change when you do.
 */
const L700MK2_RESPONSE = frCurve(
  [
    [10, -6.0],
    [20, -3.2],
    [30, -1.6],
    [40, -0.8],
    [60, -0.2],
    [80, 0.0],
    [100, 0.1],
    [150, 0.1],
    [200, 0.0],
    [300, -0.2],
    [500, -0.4],
    [700, -0.6],
    [1000, 0.0],
    [1500, 1.1],
    [2000, 2.0],
    [2500, 2.4],
    [3000, 1.6],
    [4000, -0.4],
    [5000, 0.6],
    [6000, 2.2],
    [7000, 1.4],
    [8000, -1.2],
    [9000, -2.6],
    [10000, -1.4],
    [12000, 0.4],
    [14000, 1.2],
    [16000, -2.0],
    [18000, -5.0],
    [20000, -8.5],
    [30000, -16.0],
    [41000, -22.0],
  ],
  'ESTIMATED',
  'Consensus of published third-party coupler measurements of the SR-L700MK2; replace via Measurement Lab import for a measured profile.',
);

/**
 * Diffuse-field-like compensation offered as an optional target (spec 23).
 * Applying it flattens the coupler response toward a listening-window-neutral
 * balance. Tagged ESTIMATED because the target itself is a modelled curve.
 */
const DF_TARGET = frCurve(
  [
    [20, 0], [50, 0], [100, 0], [200, 0], [500, 0], [1000, 0],
    [1500, 1.5], [2000, 3.0], [2700, 3.6], [3500, 2.4], [5000, 0.4],
    [6000, 1.0], [8000, -0.8], [10000, -1.6], [12000, -1.0], [16000, -3.0], [20000, -6.0],
  ],
  'ESTIMATED',
  'Diffuse-field-style compensation target',
);

export const STAX_SR_L700MK2: HeadphoneProfile = makeHeadphone({
  mfr: 'STAX',
  model: 'SR-L700MK2',
  price: usd(1875, 'CURRENT_RETAIL', 2024),
  year: 2019,
  country: 'Japan',
  headphoneType: 'electrostatic',
  fit: 'open',
  response: L700MK2_RESPONSE,
  target: { name: 'Diffuse-field (modelled)', curve: DF_TARGET, confidence: 'ESTIMATED' },
  biasVolts: 580,
  capacitancePf: 110,
  sensitivityDbVrms: 101,
  thdPercent: 0.035,
  requiresEnergiser: true,
  estatBias: 'pro-580v',
  // Open dipole-ish housing: seal barely matters, unlike a closed dynamic can.
  sealSensitivityDb: 0.8,
  harmonicShape: HARMONIC_SHAPES.electrostatic,
  nonlinear: {
    kind: 'electrostatic',
    amount: 0.42,
    // Constant-charge drive is close to symmetric; the small residual asymmetry
    // comes from diaphragm offset within the stator gap.
    asymmetry: 0.05,
    bias: -0.65,
    headroomDb: 3.5,
    oversample: 4,
  },
  channels: {
    // STAX matches drivers by hand; unit-to-unit channel matching is good but
    // an electrostatic transducer has no shared magnet structure to couple
    // channels, so acoustic crosstalk is essentially just head diffraction.
    imbalanceDb: 0.12,
    crosstalkDb: -46,
    crosstalkSlopeDbDec: 0,
    skewSamples: 0,
  },
  suggestedCrossfeed: 'low',
  confidence: 'ESTIMATED',
  accent: '#a8d8ff',
  tags: ['electrostatic', 'stax', 'lambda', 'pro-bias', 'default-system', 'requires-energiser'],
  notes:
    'Lambda-series rectangular electrostatic element on 580 V pro bias, roughly 110 pF including the cable. In this engine the L700MK2 forces the headphone signal path: the loudspeaker room model, baffle step and directivity stages are disabled and replaced by crossfeed, interaural processing and headphone EQ. It also requires an amplifier whose profile declares electrostatic drive on the matching bias standard -- pairing it with a conventional headphone amplifier is flagged as an invalid chain rather than silently simulated.',
  provenanceNote:
    'Bias voltage, capacitance and sensitivity from STAX published specifications. The frequency response is an ESTIMATED consensus shape from published third-party coupler measurements; distortion and the nonlinear model are MODELLED from constant-charge electrostatic behaviour. No unit was measured for this project.',
});

export const HEADPHONES_00: HeadphoneProfile[] = [STAX_SR_L700MK2];
